<template>
  <v-main class="feedback-page" dir="rtl">
    <v-container class="feedback-container">
      <header class="page-brand">
        <img src="@/assets/logo/Logo.svg" alt="سیمیـاروم" width="136" height="26">
        <span>همراه شما در مسیر بهتر شدن</span>
      </header>
      <v-card flat class="feedback-card" :aria-busy="loading || saving">
        <div class="feedback-heading">
          <h1>بازخورد جلسه</h1>
          <p>بازخورد شما به شناخت بهتر روند جلسات کمک می‌کند.</p>
        </div>
        <div v-if="loading" class="feedback-state" role="status">
          <v-progress-circular indeterminate color="primary" aria-label="در حال دریافت فرم" />
          <p>در حال دریافت فرم…</p>
        </div>
        <div v-else-if="unavailable" class="feedback-state">
          <v-alert type="info" text>این لینک در دسترس نیست یا دیگر اعتبار ندارد.</v-alert>
        </div>
        <div v-else-if="stage === 'completed'" class="feedback-state">
          <v-alert type="success" text role="status">بازخورد این جلسه تکمیل شده است. از همراهی شما سپاسگزاریم.</v-alert>
          <v-btn depressed color="#21865f" class="feedback-submit" :href="dashboardPath" rel="noreferrer">رفتن به داشبورد</v-btn>
        </div>
        <div v-else-if="justSubmittedORS" class="feedback-state">
          <v-alert type="success" text role="status">ارزیابی قبل از جلسه شما تکمیل شد</v-alert>
          <p>برای ارزیابی بعد از جلسه، پس از برگزاری جلسه دوباره همین لینک را باز کنید.</p>
          <v-btn depressed color="primary" class="feedback-submit" :href="dashboardPath" rel="noreferrer">رفتن به داشبورد</v-btn>
        </div>
        <template v-else>
          <v-alert v-if="error" type="error" text role="alert">{{ error }}</v-alert>
          <v-btn v-if="!stage" outlined @click="load">تلاش دوباره</v-btn>
          <template v-else-if="!ready">
            <v-alert type="info" text>فرم بازخورد در حال آماده‌سازی است. لطفاً بعداً دوباره مراجعه کنید.</v-alert>
            <v-btn outlined @click="load">تلاش دوباره</v-btn>
          </template>
          <v-form v-else class="feedback-form" @submit.prevent="send(false)">
            <ol class="feedback-steps" aria-label="مراحل بازخورد">
              <li :class="{ 'is-active': stage === 'ors', 'is-done': stage === 'srs' }" :aria-current="stage === 'ors' ? 'step' : null">
                <span class="step-number" aria-hidden="true">{{ stage === 'srs' ? '✓' : '۱' }}</span>
                <span>قبل از جلسه</span>
              </li>
              <li :class="{ 'is-active': stage === 'srs' }" :aria-current="stage === 'srs' ? 'step' : null">
                <span class="step-number" aria-hidden="true">۲</span>
                <span>بعد از جلسه</span>
              </li>
            </ol>
            <div class="questionnaire-heading">
              <div>
                <p class="stage-caption">{{ stage === 'ors' ? 'بخش اول از دو بخش' : 'بخش دوم از دو بخش' }}</p>
                <h2>{{ questionnaire.title }}</h2>
              </div>
              <span class="question-count">۴ سؤال</span>
            </div>
            <p v-if="questionnaire.instructions" class="question-instructions"><span v-for="(part, index) in instructionParts" :key="index"><strong v-if="part.emphasis">{{ part.text }}</strong><template v-else>{{ part.text }}</template></span></p>
            <div class="question-grid">
              <fieldset v-for="(question, index) in questionnaire.questions" :key="`${stage}-${index}`" class="question" :class="{ 'has-answer': answers[index] !== null }" :disabled="saving">
                <legend>{{ question.text }}</legend>
                <div class="question-body">
                  <div class="question-meta">
                    <span>سؤال {{ formatNumber(index + 1) }}</span>
                    <span class="answer-value" aria-live="polite">{{ answers[index] === null ? 'انتخاب نشده' : 'انتخاب شد' }}</span>
                  </div>
                  <div class="scale-layout">
                    <div class="scale-endpoints">
                      <span data-score="0" dir="rtl">{{ question.low.trim() }}</span>
                      <span data-score="10" dir="rtl">{{ question.high.trim() }}</span>
                    </div>
                    <feedback-scale
                      class="scale-control"
                      :value="answers[index] === null ? 5 : answers[index]"
                      :label="question.text" :step="questionnaire.step || 1"
                      :disabled="saving" :selected="answers[index] !== null"
                      @input="$set(answers, index, Number($event))"
                      @change="$set(answers, index, Number($event))"
                    />
                  </div>
                </div>
              </fieldset>
            </div>
            <footer class="feedback-actions">
              <div class="answer-progress" role="status">
                <span>{{ formatNumber(answeredCount) }} از ۴ پاسخ</span>
                <span class="progress-dots" aria-hidden="true"><i v-for="n in 4" :key="n" :class="{ filled: n <= answeredCount }" /></span>
              </div>
              <div class="action-buttons">
                <v-btn type="submit" depressed color="#21865f" class="feedback-submit" :loading="saving" :disabled="!answered || saving">ثبت پاسخ‌ها</v-btn>
                <v-btn text class="feedback-skip" :disabled="saving" @click="skipDialog = true">رد کردن این بخش</v-btn>
              </div>
            </footer>
          </v-form>
        </template>
      </v-card>
      <v-dialog v-model="skipDialog" max-width="420" :persistent="saving">
        <v-card class="skip-card" dir="rtl">
          <h2>این بخش رد شود؟</h2>
          <p>پس از رد کردن این بخش، امکان ثبت پاسخ برای آن وجود ندارد.</p>
          <v-card-actions>
            <v-btn text :disabled="saving" @click="skipDialog = false">بازگشت</v-btn>
            <v-btn color="primary" :loading="saving" :disabled="saving" @click="send(true)">رد کردن بخش</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import { completeAnswers, feedbackStage, loadFeedback, loadQuestionnaires, questionnaireReady, submitFeedback, validFeedbackUuid } from '@/services/therapyFeedback'
import FeedbackScale from '@/components/Feedback/FeedbackScale.vue'

export default {
  name: 'TherapyFeedback',
  components: { FeedbackScale },
  data: () => ({ loading: true, saving: false, unavailable: false, status: null, error: '', questionnaires: {}, answers: [null, null, null, null], skipDialog: false, justSubmittedORS: false, requestId: 0 }),
  metaInfo: { title: 'بازخورد جلسه', meta: [{ name: 'referrer', content: 'no-referrer' }, { name: 'robots', content: 'noindex,nofollow' }] },
  computed: {
    stage() { return feedbackStage(this.status) },
    dashboardPath() { return `/${this.$route.params.lang === 'en' ? 'en' : 'fa'}/panel/dashboard` },
    questionnaire() { return this.questionnaires[this.stage] },
    instructionParts() {
      const instructions = (this.questionnaire && this.questionnaire.instructions) || ''
      return instructions.split(/(وضعیت نامطلوب‌تر|وضعیت مطلوب‌تر)/g)
        .map(text => ({ text, emphasis: ['وضعیت نامطلوب‌تر', 'وضعیت مطلوب‌تر'].includes(text) }))
    },
    ready() { return questionnaireReady(this.questionnaire) },
    answered() { return completeAnswers(this.answers) },
    answeredCount() { return this.answers.filter(value => value !== null).length }
  },
  watch: { '$route.params.uuid': 'load' },
  created() { this.load() },
  methods: {
    formatNumber(value) { return new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 2 }).format(value) },
    async load() {
      const requestId = ++this.requestId
      const uuid = this.$route.params.uuid
      this.loading = true
      this.error = ''
      this.unavailable = false
      this.status = null
      this.justSubmittedORS = false
      this.answers = [null, null, null, null]
      if (!validFeedbackUuid(this.$route.params.uuid)) {
        this.unavailable = true
        this.loading = false
        return
      }
      try {
        const status = await loadFeedback(uuid)
        if (requestId !== this.requestId) return
        this.status = status
        if (!feedbackStage(this.status)) throw new Error('Unexpected feedback status')
        if (this.status !== 'completed') this.questionnaires = await loadQuestionnaires()
      } catch (error) {
        if (requestId === this.requestId) this.handleError(error)
      } finally { if (requestId === this.requestId) this.loading = false }
    },
    handleError(error) {
      const code = error.response && error.response.status
      if ([404, 410].includes(code)) this.unavailable = true
      else if (code === 429) this.error = 'تعداد درخواست‌ها زیاد است. کمی بعد دوباره تلاش کنید.'
      else if (code === 422) this.error = 'لطفاً پاسخ هر چهار مورد را بررسی کرده و دوباره تلاش کنید.'
      else this.error = 'ارتباط برقرار نشد. لطفاً دوباره تلاش کنید.'
    },
    async send(skip) {
      if (this.saving || !this.ready || !['ors', 'srs'].includes(this.stage) || (!skip && !this.answered)) return
      const uuid = this.$route.params.uuid
      const instrument = this.stage
      this.saving = true
      this.error = ''
      const payload = skip ? { status: `${this.stage}_skipped` } : { [`${this.stage}_results`]: this.answers.slice() }
      try {
        const status = await submitFeedback(uuid, payload)
        if (uuid !== this.$route.params.uuid) return
        this.status = status
        this.justSubmittedORS = instrument === 'ors' && !skip && feedbackStage(status) === 'srs'
        this.answers = [null, null, null, null]
      } catch (error) {
        if (uuid !== this.$route.params.uuid) return
        if (error.response && error.response.status === 409) {
          // Another tab may have completed this stage. Refresh without overwriting it.
          try {
            const status = await loadFeedback(uuid)
            if (uuid !== this.$route.params.uuid) return
            this.status = status
            this.answers = [null, null, null, null]
          } catch (refreshError) { this.handleError(refreshError) }
        } else this.handleError(error)
      } finally {
        this.skipDialog = false
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.feedback-page { min-height: 100vh; background: #f3f7f5; }
.feedback-container { width: 100%; max-width: 1040px; padding: 32px 24px 48px; }
.page-brand { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin: 0 4px 24px; }
.page-brand img { display: block; max-width: 136px; }
.page-brand > span { color: #667b73; font-size: 13px; }
.feedback-card { padding: 28px; border: 1px solid #e1e9e4; border-radius: 24px !important; box-shadow: 0 12px 40px #243d3208 !important; }
.feedback-heading { margin-bottom: 20px; }
.feedback-heading h1 { margin: 0 0 6px; color: #203d33; font-size: 26px; font-weight: 700; line-height: 1.6; }
.feedback-heading p { margin: 0; color: #64746d; font-size: 15px; }
.feedback-steps { display: flex; gap: 8px; list-style: none; padding: 0; margin: 0 0 20px; }
.feedback-steps li { display: flex; flex: 1; align-items: center; justify-content: center; gap: 10px; min-width: 0; padding: 10px; border-radius: 12px; background: #f5f7f6; color: #7b8983; font-size: 14px; font-weight: 500; }
.feedback-steps li.is-active { background: #eaf6ef; color: #21734f; }
.feedback-steps li.is-done { color: #21734f; }
.step-number { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 26px; width: 26px; height: 26px; border: 1px solid #dce5df; border-radius: 50%; background: #fff; font-size: 13px; }
.is-active .step-number, .is-done .step-number { color: #fff; background: #21865f; border-color: #21865f; }
.questionnaire-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.stage-caption { margin: 0 0 2px; color: #718078; font-size: 12px; }
.questionnaire-heading h2 { margin: 0; color: #213d33; font-size: 21px; font-weight: 700; line-height: 1.7; }
.question-count { flex-shrink: 0; padding: 4px 12px; color: #64746d; background: #f4f7f5; border-radius: 8px; font-size: 13px; }
.question-instructions { margin: 14px 0 24px; color: #586e62; font-size: 15px; white-space: pre-line; line-height: 2.1; }
.question-instructions strong { color: #294a3a; font-weight: 700; }
.question-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 20px; }
.question { min-width: 0; margin: 0; padding: 22px 24px; border: 0; border-radius: 16px; background: #fafcfb; box-shadow: inset 0 0 0 1px #e0e9e3; transition: box-shadow .15s, background .15s; }
.question.has-answer { background: #f7fcf9; box-shadow: inset 0 0 0 1px #add6bf; }
.question:focus-within { outline: 2px solid #21865f; outline-offset: 3px; }
.question legend { float: right; width: 100%; max-width: 100%; padding: 0; margin-bottom: 12px; color: #2b4036; font-size: 16px; font-weight: 600; line-height: 1.85; white-space: pre-line; text-align: center; }
.question-body { clear: both; }
.question-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; color: #738178; font-size: 12px; }
.answer-value { padding: 3px 10px; border-radius: 7px; background: #edf1ee; color: #6b7971; font-weight: 500; }
.has-answer .answer-value { color: #21734f; background: #e3f3e9; }
.scale-layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(220px, 1.7fr) minmax(0, 1fr); align-items: center; gap: 18px; direction: ltr; }
.scale-endpoints { display: contents; color: #586b60; font-size: 14px; line-height: 2; }
.scale-endpoints span { min-width: 0; white-space: pre-line; overflow-wrap: anywhere; text-align: center; }
.scale-endpoints [data-score="0"] { grid-column: 1; grid-row: 1; }
.scale-endpoints [data-score="10"] { grid-column: 3; grid-row: 1; }
.scale-control { grid-column: 2; grid-row: 1; min-width: 0; width: 100%; }
.scale-control ::v-deep .v-slider { min-height: 48px; margin: 0 10px; }
.scale-control ::v-deep .v-slider__track-container { height: 6px; }
.scale-control ::v-deep .v-slider__track-background, .scale-control ::v-deep .v-slider__track-fill { border-radius: 8px; }
.scale-control ::v-deep .v-slider__thumb { width: 20px; height: 20px; left: -10px; box-shadow: 0 0 0 4px #fff; }
.feedback-actions { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-top: 24px; margin-top: 24px; border-top: 1px solid #e6ece8; }
.answer-progress { display: flex; flex-direction: column; gap: 6px; color: #6b7d71; font-size: 13px; }
.progress-dots { display: flex; gap: 5px; }
.progress-dots i { display: block; width: 22px; height: 4px; border-radius: 4px; background: #e1e8e3; }
.progress-dots i.filled { background: #21865f; }
.action-buttons { display: flex; align-items: center; gap: 10px; }
.feedback-submit.v-btn { height: 48px; min-width: 152px; padding: 0 24px; border-radius: 12px; color: #fff; font-size: 15px; font-weight: 500; }
.feedback-skip.v-btn { min-height: 44px; color: #617269; font-size: 14px; }
.feedback-state { padding: 24px 0 8px; color: #64746d; font-size: 16px; line-height: 2; }
.feedback-state[role="status"] { text-align: center; }
.feedback-state .v-alert { border-radius: 12px; line-height: 2; }
.skip-card { padding: 28px; border-radius: 20px !important; font-size: 15px; line-height: 2; }
.skip-card h2 { margin-bottom: 12px; font-size: 21px; font-weight: 700; }
@media (max-width: 759px) {
  .scale-layout { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 20px; }
  .scale-endpoints [data-score="10"] { grid-column: 2; }
  .scale-control { grid-column: 1 / -1; grid-row: 2; }
}
@media (max-width: 599px) {
  .feedback-container { padding: 20px 12px 28px; }
  .page-brand { margin: 0 4px 18px; }
  .page-brand img { width: 120px; height: auto; }
  .page-brand > span { max-width: 130px; font-size: 11px; text-align: left; }
  .feedback-card { padding: 20px 16px; border-radius: 20px !important; }
  .feedback-heading h1 { font-size: 25px; }
  .feedback-heading p { font-size: 14px; line-height: 1.9; }
  .feedback-steps { margin-bottom: 20px; }
  .feedback-steps li { gap: 8px; padding: 10px 8px; font-size: 13px; }
  .questionnaire-heading h2 { font-size: 18px; }
  .question-count { padding: 3px 9px; font-size: 12px; }
  .question-instructions { margin-top: 12px; font-size: 14px; line-height: 2; }
  .question { padding: 18px 16px; }
  .question legend { font-size: 15px; }
  .scale-endpoints { font-size: 13px; }
  .feedback-actions { flex-direction: column; align-items: stretch; gap: 18px; margin-top: 20px; padding-top: 20px; }
  .answer-progress { flex-direction: row; align-items: center; justify-content: space-between; }
  .action-buttons { flex-direction: column; gap: 6px; }
  .feedback-submit.v-btn, .feedback-skip.v-btn { width: 100%; }
}
</style>
